import { batch } from 'react-redux';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { captureException } from 'fe-core/util/errorTracking';

import { getCurrentUserIsManagerial } from 'selectors/session';

import { fetchSchedule } from 'features/scheduleBuilder/ScheduleBuilderView/actions/shared';
import { loadJobTotals } from 'features/scheduleBuilder/ScheduleBuilderView/slice';
import { actions as claimRequestActions } from 'features/scheduleBuilder/slices/claimRequests/slice';
import { removeAll as removeAllConflicts } from 'features/scheduleBuilder/slices/conflicts/slice';
import { actions as estimatedSalesActions } from 'features/scheduleBuilder/slices/estimatedSales/slice';
import { actions as eventQuotaActions } from 'features/scheduleBuilder/slices/eventQuotas/slice';
import { actions as eventActions } from 'features/scheduleBuilder/slices/events/slice';
import { actions as laborTargetsActions } from 'features/scheduleBuilder/slices/laborTargets/slice';
import { actions as roleLaborTotalsActions } from 'features/scheduleBuilder/slices/roleLaborTotals/slice';
import { actions as rolesActions } from 'features/scheduleBuilder/slices/roles/slice';
import { actions as scheduleAvailabilityActions } from 'features/scheduleBuilder/slices/scheduleAvailabilities/slice';
import { actions as shiftEventsActions } from 'features/scheduleBuilder/slices/shiftEvents/slice';
import { actions as shiftNotesQuotaActions } from 'features/scheduleBuilder/slices/shiftNotesQuotas/slice';
import { removeAll as removeAllShifts } from 'features/scheduleBuilder/slices/shifts/slice';
import { actions as timeOffActions } from 'features/scheduleBuilder/slices/timeOffs/slice';
import { actions as weatherForecastActions } from 'features/scheduleBuilder/slices/weatherForecast/slice';

import { getTimeDifference } from 'util/dateTime';

import { selectSliceAttribute } from '../helpers';
import { selectJobIds } from '../jobs/slice';

export const clearScheduleEntities = () => (dispatch: any) => {
  batch(() => {
    dispatch(shiftEventsActions.removeAll());
    dispatch(estimatedSalesActions.removeAll());
    dispatch(laborTargetsActions.removeAll());
    dispatch(roleLaborTotalsActions.removeAll());
    dispatch(scheduleAvailabilityActions.removeAll());
    dispatch(shiftNotesQuotaActions.removeAll());
    dispatch(removeAllShifts());
    dispatch(eventActions.removeAll());
    dispatch(eventQuotaActions.removeAll());
    dispatch(timeOffActions.removeAll());
    dispatch(removeAllConflicts());
    dispatch(claimRequestActions.removeAll());
    dispatch(rolesActions.removeAll());
    dispatch(weatherForecastActions.clearAllEntities());
  });
};

export const reloadSchedule = createAsyncThunk(
  'scheduleBuilder/reloadSchedule',
  async (
    {
      startDate,
      endDate,
      force = true,
    }: { startDate: string; endDate: string; force?: boolean },
    { dispatch, getState }
  ) => {
    const state = getState();
    const currentScheduleStartDate = selectSliceAttribute({
      state,
      attribute: 'startDate',
      sliceName: 'schedule',
    });
    const currentScheduleEndDate = selectSliceAttribute({
      state,
      attribute: 'endDate',
      sliceName: 'schedule',
    });

    // date range is outside of the current one
    if (
      force ||
      getTimeDifference(currentScheduleStartDate, startDate, 'days') < 0 ||
      getTimeDifference(currentScheduleEndDate, endDate, 'days') > 0
    ) {
      dispatch(clearScheduleEntities());
      const isManager = getCurrentUserIsManagerial(state);
      let res: any;

      return dispatch(
        fetchSchedule({
          startDate,
          endDate,
        })
      )
        .then((response: any) => {
          // If request to fetch schedule fails - go back to the previous date range
          res = response;
          if (response?.error) {
            dispatch(clearScheduleEntities());
            return;
          }
          if (isManager) {
            dispatch(
              loadJobTotals(
                response?.payload?.data?.relationships?.jobs?.data?.map(
                  (j: any) => j.id
                ) ||
                  selectJobIds(state) ||
                  []
              )
            );
          }
        })
        .catch((error: unknown) => {
          console.error(error);
          captureException(error, {
            extra: {
              payload: JSON.stringify(res),
            },
          });
        });
    }

    return Promise.resolve();
  }
);
